import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useReducer, useTransition } from "react";
import SignalContext from "../contexts/index.js";
import Reducer from "./reducer.js";
/**
 * Provides a React context for managing global signal state using a reducer.
 *
 * The `SignalProvider` component wraps the application and provides a context
 * with methods for dispatching synchronous and asynchronous actions to update
 * the global signal state.
 *
 * The provider uses the `useReducer` hook to manage the global state, and the
 * `useTransition` hook to wrap the dispatch function and provide a smooth
 * user experience during state updates.
 *
 * The provider also exposes an `asyncDispatch` function that can be used to
 * dispatch asynchronous actions, which are handled by updating the state of
 * the corresponding signal.
 *
 * @param {ProviderProps} props - The props for the `SignalProvider` component.
 * @param {React.ReactNode} props.children - The child components to wrap with the provider.
 * @param {any} props.store - The initial store for the signal state.
 * @returns {React.ReactElement} - The `SignalProvider` component.
 */
export default function SignalProvider(_a) {
    var children = _a.children, store = _a.store;
    // Global state that manage all signals
    var _b = useReducer(Reducer, store.getSignals()), signals = _b[0], dispatch = _b[1];
    // Wrap your dispatch function with useTransition
    var _c = useTransition(), startTransition = _c[1];
    // Your state management logic using useContext and useReducer
    var syncDispatch = function (action) {
        startTransition(function () {
            dispatch(action);
        });
    };
    // Dispatch async actions
    var asyncDispatch = useCallback(function (action) {
        var signalName = action.type.split("/")[0];
        var newState = signals.map(function (_a) {
            var name = _a.name, operations = _a.operations, actions = _a.actions, asyncActions = _a.asyncActions, prevState = _a.state;
            var state = prevState;
            // Capture the target signal (a state and a bunch of async actions) from the array of signals.
            // Capture the action from array of async actions (of the target signal).
            // Run the async action and update the signal state.
            if (name === signalName) {
                if (action.isAsync) {
                    for (var _i = 0, asyncActions_1 = asyncActions; _i < asyncActions_1.length; _i++) {
                        var _b = asyncActions_1[_i], type = _b.type, steps = _b.steps;
                        if (type === action.type) {
                            state = steps.cases
                                .find(function (c) { return c.status === action.status; })
                                .handler(state, action.payload);
                            break;
                        }
                    }
                }
            }
            return {
                name: name,
                operations: operations,
                state: state,
                actions: actions,
                asyncActions: asyncActions,
            };
        });
        // Find the new state of the target signal
        var signal = newState.find(function (signal) { return signal.name === signalName; });
        dispatch({
            type: action.type,
            isAsync: action.isAsync,
            status: action.status,
            payload: signal.state,
        });
        return signal.state;
    }, []);
    // Context value
    var contextValue = {
        signals: signals,
        dispatch: syncDispatch,
        asyncDispatch: asyncDispatch,
    };
    return (_jsx(SignalContext.Provider, { value: contextValue, children: children }));
}
